import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Clock,
  Flame,
  Hammer,
  Inbox,
  ShieldCheck,
} from "lucide-react";
import { api, type ManagementQueueRow } from "../api";
import { Card, PageHeader, PageHeaderTab, StatusBadge, cx } from "../ui";
import { useAuth } from "../auth";

type Bucket =
  | "triage"
  | "submitted"
  | "in_review"
  | "owner_action"
  | "board"
  | "approved";

const BUCKETS: Array<{
  key: Bucket;
  label: string;
  icon: React.ReactNode;
  matches: (r: ManagementQueueRow) => boolean;
}> = [
  {
    // \"Triage\" cuts across statuses and surfaces only the rows the
    // server scored as actually urgent. This is the lead tab so PMs
    // see what to do first instead of FIFO.
    key: "triage",
    label: "Triage",
    icon: <Flame size={14} />,
    matches: (r) => (r.urgencyReasons.length ?? 0) > 0 || r.urgencyScore >= 200,
  },
  {
    key: "submitted",
    label: "Awaiting review",
    icon: <Inbox size={14} />,
    matches: (r) => r.statusCode === "submitted",
  },
  {
    key: "in_review",
    label: "In review",
    icon: <ClipboardList size={14} />,
    matches: (r) => r.statusCode === "under_management_review",
  },
  {
    key: "owner_action",
    label: "Awaiting owner",
    icon: <Clock size={14} />,
    matches: (r) =>
      r.statusCode === "pending_owner_revisions" ||
      r.statusCode === "resubmission_required",
  },
  {
    key: "board",
    label: "Routed to Board",
    icon: <ShieldCheck size={14} />,
    matches: (r) => r.statusCode === "under_board_review",
  },
  {
    key: "approved",
    label: "Approved & open",
    icon: <CheckCircle2 size={14} />,
    matches: (r) =>
      r.statusCode === "approved" ||
      r.statusCode === "conditionally_approved" ||
      r.statusCode === "scheduled" ||
      r.statusCode === "in_progress" ||
      r.statusCode === "inspection_required",
  },
];

export function ManagementPage() {
  const { state } = useAuth();
  const [rows, setRows] = useState<ManagementQueueRow[] | null>(null);
  const [active, setActive] = useState<Bucket>("triage");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ data: ManagementQueueRow[] }>("/api/management/queue")
      .then((r) => setRows(r.data))
      .catch((err) => setError(String(err)));
  }, []);

  const counts = useMemo(() => {
    const c: Record<Bucket, number> = {
      triage: 0,
      submitted: 0,
      in_review: 0,
      owner_action: 0,
      board: 0,
      approved: 0,
    };
    for (const r of rows ?? []) {
      // Triage is cross-cutting — count separately from the
      // per-status buckets.
      const triageMatch = BUCKETS.find((b) => b.key === "triage")!;
      if (triageMatch.matches(r)) c.triage++;
      for (const b of BUCKETS) {
        if (b.key === "triage") continue;
        if (b.matches(r)) {
          c[b.key]++;
          break;
        }
      }
    }
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const matcher = BUCKETS.find((b) => b.key === active)!.matches;
    return rows
      .filter((r) => matcher(r))
      .sort((a, b) => b.urgencyScore - a.urgencyScore);
  }, [rows, active]);

  const eyebrow =
    state.status === "authenticated"
      ? `${state.me.user.firstName} ${state.me.user.lastName}`
      : undefined;

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title="Management queue"
        subtitle="Triage projects awaiting Property Management action. Stale items (3+ days in submitted) and emergencies surface first."
        tabs={BUCKETS.map((b) => (
          <PageHeaderTab
            key={b.key}
            active={active === b.key}
            onClick={() => setActive(b.key)}
          >
            <span className="inline-flex items-center gap-1.5">
              {b.icon}
              {b.label}
              <span
                className={cx(
                  "inline-flex items-center justify-center min-w-[22px] h-[18px] px-1.5 rounded-full text-[11px] font-semibold",
                  active === b.key
                    ? "bg-brand-100 text-brand-700"
                    : "bg-white/[0.10] text-white/70",
                )}
              >
                {counts[b.key]}
              </span>
            </span>
          </PageHeaderTab>
        ))}
      />

      {error && <div className="alert-error mb-4">{error}</div>}

      {rows === null ? (
        <Card>
          <p className="text-ink-500">Loading…</p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <CheckCircle2
              size={28}
              className="text-status-approved-fg mx-auto mb-2"
            />
            <p className="text-ink-700 font-medium">Nothing in this bucket.</p>
            <p className="text-[12.5px] text-ink-500 mt-1">
              Switch tabs to see other states.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="!p-0 overflow-hidden">
          <ul className="divide-y divide-sand-100">
            {filtered.map((r) => (
              <li key={r.id} className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {r.urgencyReasons.map((reason) => (
                        <span
                          key={reason.kind}
                          className={cx(
                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold",
                            reason.severity === "critical"
                              ? "bg-status-rejected-bg text-status-rejected-fg"
                              : reason.severity === "high"
                                ? "bg-status-review-bg text-status-review-fg"
                                : "bg-sand-100 text-ink-700",
                          )}
                        >
                          {reason.kind === "emergency" ? (
                            <AlertTriangle size={11} />
                          ) : reason.kind.startsWith("voting_") ? (
                            <Clock size={11} />
                          ) : reason.kind === "contractor_sla_breach" ? (
                            <Hammer size={11} />
                          ) : (
                            <Flame size={11} />
                          )}
                          {reason.label}
                        </span>
                      ))}
                      <StatusBadge status={r.statusCode} />
                    </div>
                    <Link
                      to={`/projects/${r.id}`}
                      className="text-ink-800 font-semibold hover:text-brand-700 no-underline hover:no-underline text-[15px]"
                    >
                      {r.title}
                    </Link>
                    <div className="text-[12.5px] text-ink-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span>
                        {r.typeName} · Unit {r.unitNumber}
                      </span>
                      <span>{r.ownerName}</span>
                      {r.submittedAt && (
                        <span>
                          Submitted{" "}
                          {new Date(r.submittedAt).toLocaleDateString()}
                        </span>
                      )}
                      <span>
                        {r.daysInStatus}d in {labelForStatus(r.statusCode)}
                      </span>
                    </div>
                    {r.contractors.total > 0 && (
                      <div className="text-[11.5px] text-ink-500 mt-2 inline-flex items-center gap-1.5">
                        <Hammer size={11} />
                        <span>
                          {r.contractors.total} contractor
                          {r.contractors.total === 1 ? "" : "s"} ·{" "}
                          {r.contractors.opened}/{r.contractors.total} opened
                          link · {r.contractors.uploaded}/{r.contractors.total}{" "}
                          uploaded
                        </span>
                      </div>
                    )}
                    {r.activeStep?.votingWindowEndsAt && (
                      <div className="text-[11.5px] text-ink-500 mt-1">
                        Voting window closes{" "}
                        {new Date(
                          r.activeStep.votingWindowEndsAt,
                        ).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <Link
                    to={`/projects/${r.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[13px] text-brand-700 hover:text-brand-800 bg-brand-50 hover:bg-brand-100 border border-brand-100 rounded-md no-underline hover:no-underline whitespace-nowrap"
                  >
                    Open
                    <ArrowRight size={13} />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function labelForStatus(code: string): string {
  const map: Record<string, string> = {
    draft: "draft",
    submitted: "submitted",
    under_management_review: "review",
    pending_owner_revisions: "owner revisions",
    resubmission_required: "resubmission",
    under_board_review: "board review",
    approved: "approved",
    conditionally_approved: "conditional",
    rejected: "rejected",
    scheduled: "scheduled",
    in_progress: "in progress",
    inspection_required: "inspection",
    completed: "completed",
    closed: "closed",
  };
  return map[code] ?? code;
}
